import { FC, ReactNode, useEffect, useState } from 'react';

import { CheckboxProps, CheckedState, FieldErrorText } from '@purpur/library';

import { useComponentId } from '@telialt/utils/src/hooks';

import { CheckboxText, StyledCheckbox, StyledLabel } from './Checkbox.styles';

type Props = Omit<CheckboxProps, 'id' | 'data-testid' | 'errorText' | 'label'> & {
    dataTest: string;
    label?: ReactNode;
    errorMessage?: string;
    requiredErrorMessage?: string;
    id?: string;
};

const Checkbox: FC<Props> = ({
    id,
    errorMessage = '',
    required,
    requiredErrorMessage = '',
    onChange,
    defaultChecked = false,
    dataTest,
    label,
    disabled,
    ...props
}) => {
    const generatedId = `checkbox-${useComponentId().replace(/:/g, '')}`;
    const checkboxId = id || generatedId;
    const [isPristine, setIsPristine] = useState(true);
    const [isChecked, setIsChecked] = useState<CheckedState>(defaultChecked);

    const error = errorMessage || (required && !isChecked && !isPristine && requiredErrorMessage);

    const handleChange = (checkedState: CheckedState) => {
        setIsPristine(false);
        setIsChecked(checkedState);
        onChange?.(checkedState);
    };

    useEffect(() => {
        // Since PurPur is using Radix and there is no prop for "onInvalid" need to attach event to checkbox
        const checkbox = document.getElementById(checkboxId)?.parentElement?.querySelector('input[type=checkbox]');
        checkbox?.addEventListener('invalid', (event) => {
            event.preventDefault();
            setIsPristine(false);
        });
    }, [checkboxId]);

    return (
        <>
            <StyledLabel htmlFor={checkboxId} data-test={`${dataTest}-label`}>
                <StyledCheckbox
                    {...props}
                    required={required}
                    disabled={disabled}
                    id={id || generatedId}
                    onChange={(checked) => handleChange(checked)}
                    error={!!error}
                    data-test={dataTest}
                />
                {label && (
                    <CheckboxText data-test={`${dataTest}-label-text`} $isDisabled={disabled}>
                        {label}
                    </CheckboxText>
                )}
            </StyledLabel>
            {error && <FieldErrorText data-test={`${dataTest}-error`}>{error}</FieldErrorText>}
        </>
    );
};

export { Checkbox };
